import React, { useState, useEffect } from 'react';
import './CharacterSelection.css';
import Chat from '../Chat/chat';
import { apiCall } from '../utils/api';
import ChatHistory from './Components/ChatHistory';
import UserProfile from './Components/UserProfile';
import ContentTab from './Components/ContentTab';
import Reels from '../Reels/Reels';
import Parichay from '../Parichay/Parichay';
import Onboarding from '../Onboarding/Onboarding';
import ArticleshipList from '../Articleship/ArticleshipList';
import chaimateLogo from "../images/chaimate-logo-main.svg";
import ProfileIcon from "../images/profile-nav-icon.svg";
import HomeIcon from "../images/home-nav-icon.svg";
import ChatNavIcon from "../images/chat-nav-icon.svg";
import ReelsIcon from "../images/play-icon.svg";
import SubscriptionModal from '../components/SubscriptionModal/SubscriptionModal';
import trackEvent from '../utils/analytics';
import Loading from '../components/Loading/Loading';

const CharacterSelection = ({ darkMode, toggleDarkMode }) => {
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [error, setError] = useState(null);
    const [preselectId, setPreselectId] = useState(null);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [activeSection, setActiveSection] = useState('articleship'); // default to articleship
    const [activeTab, setActiveTab] = useState('income_experts'); // 'ai_friends' or 'income_experts'
    const [isSubscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [isNewUser, setIsNewUser] = useState(false);
    const [userProfileExists, setUserProfileExists] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [onboardingChecked, setOnboardingChecked] = useState(false);
    const [showChatNotification, setShowChatNotification] = useState(false);
    const [showPostOnboardingLoader, setShowPostOnboardingLoader] = useState(false);
    const [pendingApplication, setPendingApplication] = useState(null);
    const isContentActive = activeSection === 'content';
    const isReelsActive = activeSection === 'reels'; // now used for Feed UI
    const isFeedActive = isReelsActive;
    const isDevaActive = activeSection === 'deva';
    const isParichayActive = activeSection === 'characters';
    const isProfileActive = activeSection === 'profile';
    const isArticleshipActive = activeSection === 'articleship';
    // Only content is "immersive" (black) now. Feed should be clean/white.
    const isImmersiveActive = isContentActive;
    // Parichay has its own clean header (title + search). Do not show legacy header/tabs there.
    const shouldShowHeader = !(activeSection === 'profile' || isImmersiveActive || isDevaActive || isFeedActive || isParichayActive || isArticleshipActive);

    // Shared helper to check subscription status
    const checkSubscription = async () => {
        try {
            const resp = await apiCall.get('/subscriptions/check');
            const payload = resp?.data ?? resp;
            if (payload && typeof payload.has_subscription !== 'undefined') {
                setHasSubscription(Boolean(payload.has_subscription));
            }
        } catch (e) {
            // ignore errors; leave hasSubscription as-is
        }
    };

    useEffect(() => {
        fetchCharacters();
    }, []);

    // Check subscription status once on component load
    useEffect(() => {
        checkSubscription();
    }, []);

    // Determine whether user exists already via new API
    // NOTE: Onboarding is now triggered on first job application, not on login
    const checkIfNewUser = async () => {
        try {
            const response = await apiCall.get('/users/get/user/details');
            const payload = response.data || response;
            console.log('GET /users/get/user/details response:', payload);
            setUserDetails(payload);

            // If user signed up via scanner, default to chat section
            const signupSource = payload?.signup_source;
            const utmMedium = signupSource?.utm_medium
                || new URLSearchParams(signupSource?.referrer || '').get('utm_medium');
            if (utmMedium === 'scanner') {
                setActiveSection('deva');
            }

            const isExistingUser = Boolean(
                payload?.is_exists ??
                payload?.user_exists ??
                payload?.existing_user ??
                payload?.is_existing_user ??
                payload?.exists ??
                payload?.data?.is_existing_user
            );

            const profileExists = Boolean(
                payload?.userProfileExists ??
                payload?.user_profile_exists ??
                payload?.user_profile ??
                payload?.profile_exists ??
                payload?.onboarding_completed ??
                isExistingUser
            );

            setUserProfileExists(profileExists);
            setIsNewUser(!profileExists);
            
            setShowOnboarding(false);
            setOnboardingChecked(true);
        } catch (e) {
            console.error('Error checking if user is new:', e);
            setIsNewUser(true);
            setShowOnboarding(false);
            setOnboardingChecked(true);
        }
    };

    // Handler when user clicks Continue on job application - skip onboarding entirely
    const handleTriggerOnboardingFromApplication = async (formData, job) => {
        // Store the application data and go directly to submit mode
        setPendingApplication({ formData, job });
        console.log('Going straight to submit mode - onboarding skipped');
        setActiveSection('articleship');
    };

    // Check if user is new on component load
    useEffect(() => {
        checkIfNewUser();
    }, []);

    // Turn off post-onboarding loader after it's been shown (Loading component handles the delay internally)
    useEffect(() => {
        if (showPostOnboardingLoader) {
            // The Loading component will stay visible for 10s, then we clear the state
            const timer = setTimeout(() => {
                setShowPostOnboardingLoader(false);
            }, 10500); // Slightly longer than the 10s delay in Loading component
            return () => clearTimeout(timer);
        }
    }, [showPostOnboardingLoader]);

    // Read character id, tab, and category from URL once on mount
    useEffect(() => {
        try {
            const urlObj = new URL(window.location.href);
            const params = new URLSearchParams(urlObj.search);
            
            let characterIdFound = false;
            let tabFound = false;
            let categoryFound = false;
            
            // Decode referer parameter and extract params from it
            // Referer is query string params separated by &
            const refererEncoded = params.get('referer');
            if (refererEncoded) {
                try {
                    // Decode and parse query string params
                    const refererDecoded = decodeURIComponent(refererEncoded);
                    const refererParams = new URLSearchParams(refererDecoded);
                    
                    // Extract character_id from referer
                    const refererCharacterId = refererParams.get('character_id');
                    if (refererCharacterId) {
                        setPreselectId(refererCharacterId);
                        characterIdFound = true;
                    }
                    
                    // Extract tab from referer
                    const refererTab = refererParams.get('tab');
                    if (refererTab === 'income_experts' || refererTab === 'ai_friends') {
                        setActiveTab(refererTab);
                        tabFound = true;
                    }
                    
                    // Extract category from referer
                    const refererCategory = refererParams.get('category');
                    if (refererCategory) {
                        // Update URL with category parameter so Reels and notification logic can access it
                        if (!params.has('category')) {
                            params.set('category', refererCategory);
                        }
                        categoryFound = true;
                    }
                    
                    // Remove referer parameter from URL after extracting values
                    params.delete('referer');
                    // Update URL without reload
                    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
                    window.history.replaceState({}, '', newUrl);
                } catch (refererError) {
                    console.error('Error decoding referer:', refererError);
                }
            }
            
            // Fallback: Read character id and tab from direct URL params (if not found in referer)
            if (!characterIdFound) {
                const id = params.get('character_id');
                if (id) {
                    setPreselectId(id);
                }
            }
            
            // Read tab parameter and set active tab (if not already set from referer)
            if (!tabFound) {
                const tab = params.get('tab');
                if (tab === 'income_experts' || tab === 'ai_friends') {
                    setActiveTab(tab);
                }
            }
            
            // Read category parameter from direct URL params (if not already set from referer)
            // Category is already being read in notification logic and Reels component
            // This ensures it's available in the URL for those components
        } catch (e) {
            // no-op
        }
    }, []);

    // When characters are loaded, preselect if URL id matches
    useEffect(() => {
        if (!preselectId || characters.length === 0 || selectedCharacter) return;
        const match = characters.find(c => String(c?._id) === String(preselectId) || String(c?.id) === String(preselectId));
        if (match) {
            setSelectedCharacter(match);
            // Remove character_id from URL after redirecting/opening chat
            try {
                const url = new URL(window.location.href);
                url.searchParams.delete('character_id');
                const newSearch = url.searchParams.toString();
                const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash;
                window.history.replaceState({}, '', newUrl);
                setPreselectId(null);
            } catch (e) {
                // no-op
            }
        }
    }, [preselectId, characters, selectedCharacter]);

    const fetchCharacters = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiCall.get('/character/profile/list');
            
            // Debug: Log API response
            console.log('API Response:', response);
            
            // Assuming the API returns data in a structure like { data: [...] }
            // Add default tags for testing if they don't exist
            const characterData = response.data || response;
            setCharacters(characterData);
        } catch (err) {
            console.error('Error fetching characters:', err);
            setError('Failed to load characters. Please try again.');
            // Fallback to empty array to prevent crashes
            setCharacters([]);
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }
    };


    const handleCharacterSelect = (character) => {
        setSelectedCharacter(character);
    };

    const handleBackToCharacterSelection = async () => {
        setSelectedCharacter(null);
        await checkSubscription();
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // Track tab change event to Facebook
        trackEvent('tab_changed', {
            tab_name: tab,
            is_new_user: isNewUser,
            previous_tab: activeTab
        });
    };

    const handleSectionChange = (section) => {
        setActiveSection(section);
        
        // Track tab view events
        if (section === 'reels') {
            trackEvent('feed_tab_viewed', { tab: 'feed' });
        } else if (section === 'deva') {
            trackEvent('chat_tab_viewed', { tab: 'chat' });
            setShowChatNotification(false);
        } else if (section === 'articleship') {
            trackEvent('articleship_tab_viewed', { tab: 'articleship' });
        }
    };

    const handleProfileClick = () => {
        setActiveSection('profile');
        trackEvent('profile_tab_clicked', { tab: 'profile' });
    };

    // Handle onboarding completion
    const persistOnboardingData = async (profileData) => {
        try {
            await apiCall.post('/users/onboarding', profileData);
        } catch (error) {
            console.error('Error saving onboarding data:', error);
        }

        try {
            await apiCall.post('/users/get/user/onboarding', {
                onboarding_completed: true
            });
        } catch (error) {
            console.error('Error flagging onboarding completion:', error);
        }
    };

    const handleOnboardingComplete = (profileData) => {
        setShowOnboarding(false);
        setUserDetails(prev => ({ ...prev, ...profileData, onboarding_completed: true }));
        setUserProfileExists(true);
        void persistOnboardingData(profileData);
        
        // If there's a pending job application, return to articleship to submit it
        if (pendingApplication) {
            setActiveSection('articleship');
        } else {
            setActiveSection('articleship');
            setShowPostOnboardingLoader(true);
        }
        
        try {
            localStorage.removeItem('is_new_user');
        } catch (e) {
            // ignore storage errors
        }
        
        setTimeout(() => {
            setShowChatNotification(true);
        }, 60000);
        
        trackEvent('onboarding_flow_completed', {
            has_goals: profileData.goals?.length > 0,
            has_role: Boolean(profileData.current_role)
        });
    };

    // Show onboarding for new users who haven't completed it
    if (showOnboarding) {
        return (
            <Onboarding
                onComplete={handleOnboardingComplete}
                existingData={userDetails || {}}
            />
        );
    }

    // If a character is selected, show the chat
    if (selectedCharacter) {
        return (
            <Chat
                isUnidentifiedUser={false}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                selectedCharacter={selectedCharacter}
                onBackToCharacterSelection={handleBackToCharacterSelection}
                hasSubscription={hasSubscription}
            />
        );
    }


    // Show character selection screen
    const renderContent = () => {
        if (activeSection === 'history') {
            return <ChatHistory onCharacterSelect={handleCharacterSelect} activeTab={activeTab} allCharacters={characters} userDetails={userDetails} />;
        } else if (activeSection === 'profile') {
            return (
                        <UserProfile
                            hasSubscription={hasSubscription}
                            onOpenSubscriptionModal={() => {/* setSubscriptionModalOpen(true) */}}
                            onboardingData={userDetails}
                            onBackFromProfile={() => setActiveSection('articleship')}
                        />
            );
        } else if (isDevaActive) {
            return (
                <ChatHistory
                    onCharacterSelect={handleCharacterSelect}
                    activeTab="all"
                    pinnedCharacterNames={['Devang']}
                    allCharacters={characters}
                    userDetails={userDetails}
                />
            );
        } else if (isReelsActive) {
            return (
                <Reels
                    characters={characters}
                    onOpenChat={handleCharacterSelect}
                    onOpenProfile={() => setActiveSection('profile')}
                />
            );
        } else if (isContentActive) {
            return (
                <ContentTab
                    characters={characters}
                    onOpenChat={handleCharacterSelect}
                    onOpenProfile={() => setActiveSection('profile')}
                />
            );
        } else if (activeSection === 'articleship') {
            return (
                <ArticleshipList 
                    onTriggerOnboarding={handleTriggerOnboardingFromApplication}
                    pendingApplication={pendingApplication}
                    onClearPendingApplication={() => setPendingApplication(null)}
                />
            );
        } else {
            return <Parichay />;
        }
    };

    return (
        <div className={`CharacterSelection ${isImmersiveActive ? 'content-mode-root' : ''} ${isDevaActive ? 'deva-mode-root' : ''} ${isFeedActive ? 'feed-mode-root' : ''} ${isParichayActive ? 'parichay-mode-root' : ''} ${isProfileActive ? 'profile-mode-root' : ''} ${isArticleshipActive ? 'articleship-mode-root' : ''}`}>
            {/* Loading overlay - full variant for initial load or post-onboarding, simple for page transitions */}
            <Loading isLoading={loading || showPostOnboardingLoader} variant={(isInitialLoad || showPostOnboardingLoader) ? 'full' : 'simple'} />
            
            <div className={`character-selection-container ${isImmersiveActive ? 'content-mode' : ''} ${isFeedActive ? 'feed-mode' : ''} ${isParichayActive ? 'parichay-mode' : ''} ${isProfileActive ? 'profile-mode' : ''} ${isArticleshipActive ? 'articleship-mode' : ''}`}>
                {/* Static Header Section - Hide when profile or content is active */}
                {shouldShowHeader && (
                    <div className="character-selection-header">
                        <div className="logo-section">
                            <img src={chaimateLogo} alt="ChaiMate Logo" />
                            <div>ChaiMate</div>
                            {hasSubscription && (<span className="pro-badge">PRO</span>)}
                        </div>
                        
                        {/* Tab Navigation */}
                        <div className="tab-navigation">
                            <div className="tab-scroll-container">
                                {!isNewUser && (
                                    <button 
                                        className={`tab-button ${activeTab === 'ai_friends' ? 'active' : ''}`}
                                        onClick={() => handleTabChange('ai_friends')}
                                    >
                                        AI Friends
                                    </button>
                                )}
                                <button 
                                    className={`tab-button ${activeTab === 'income_experts' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('income_experts')}
                                >
                                    Income Experts
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Scrollable Content Section */}
                <div className={`character-selection-content ${isContentActive ? 'content-section-active' : ''} ${isReelsActive ? 'reels-section-active' : ''} ${isParichayActive ? 'parichay-section-active' : ''} ${isProfileActive ? 'profile-section-active' : ''} ${isDevaActive ? 'deva-section-active' : ''} ${isArticleshipActive ? 'articleship-section-active' : ''}`}>
                    {!isProfileActive && (
                        <button 
                            className="global-profile-btn"
                            onClick={handleProfileClick}
                            aria-label="Open Profile"
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </button>
                    )}
                    {renderContent()}
                </div>

                {/* Footer Navigation — Tab order: Feed → Parichay → Devang → Profile */}
                <div className="footer-navigation">
                    <button
                        className={`footer-tab-button ${isReelsActive ? 'active' : ''}`}
                        onClick={() => handleSectionChange('reels')}
                        aria-label="Open Feed"
                    >
                        <span className={`footer-icon-wrapper ${isReelsActive ? 'active' : ''}`}>
                        <img src={ReelsIcon} alt="Feed" className="footer-icon" />
                        </span>
                        <span>Feed</span>
                    </button>
                    <button 
                        className={`footer-tab-button ${isDevaActive ? 'active' : ''}`}
                        onClick={() => handleSectionChange('deva')}
                        style={{ position: 'relative' }}
                    >
                        <span className={`footer-icon-wrapper ${isDevaActive ? 'active' : ''}`}>
                        <img src={ChatNavIcon} alt="Chats" className="footer-icon" />
                        </span>
                        <span>Chats</span>
                        {showChatNotification && (
                            <span style={{
                                position: 'absolute',
                                top: '6px',
                                right: '12px',
                                width: '10px',
                                height: '10px',
                                backgroundColor: '#22C55E',
                                borderRadius: '50%',
                                border: '2px solid #ffffff',
                            }} />
                        )}
                    </button>
                    <button 
                        className={`footer-tab-button ${activeSection === 'articleship' ? 'active' : ''}`}
                        onClick={() => handleSectionChange('articleship')}
                    >
                        <span className={`footer-icon-wrapper ${activeSection === 'articleship' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="footer-icon">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                            </svg>
                        </span>
                        <span>Articleship</span>
                    </button>
                </div>

                {/* Subscription Modal */}
                {/* <SubscriptionModal
                    isOpen={isSubscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    onPurchase={() => setSubscriptionModalOpen(false)}
                    title={"Recharge Pro"}
                    message={"Unlimted chats k liye recharge pro"}
                /> */}

            </div>
        </div>
    );
};

export default CharacterSelection;
